
import { supabase } from "@/integrations/supabase/client";
import { Invoice } from "@/types/payments";
import { toast } from "sonner";
import { translateOutsideReact } from '@/i18n/translateOutsideReact';
import { handleError } from "@/utils/handleError";
import { PaymentService } from "./paymentService";
import { ApprovalService } from "./approvalService";
import { isApprovalFlowEnabled } from "@/config/features";
import { InvoiceApiClient } from "@/clients/invoiceApiClient";
import type { CategoryMetadata } from "@/types/expense";
import { friendlyError } from "@/utils/friendlyError";

export type InvoiceFormData = {
  file: File;
  vendor: string;
  amount: number;
  currency: string;
  dueDate: string;
  transactionDate?: string;
  documentType: 'payable' | 'receivable' | 'receipt' | 'expense';
  remittanceInformation: string;
  // Payable fields
  vendorAccountFields?: {
    vendor_iban?: string | null;
    vendor_bankgiro?: string | null;
    vendor_plusgiro?: string | null;
    vendor_account_number?: string | null;
    vendor_clearing_number?: string | null;
  } | null;
  sourceAccountId?: string;
  payablePaymentDate?: string;
  bankBic?: string;
  bankName?: string;
  // Receipt fields
  paymentMethod?: 'card' | 'cash' | 'bank_transfer' | 'salary_adjustment';
  expenseCategory?: string;
  categoryMetadata?: CategoryMetadata;
};

/** What finalizing a draft can set: the form's fields, plus the sales-report fields of a Z-report. */
export type DraftFinalizeUpdates = Partial<Omit<InvoiceFormData, "documentType">> & {
  documentType?: InvoiceFormData["documentType"] | "sales_report";
  reportDate?: string;
  totalGrossSales?: number;
  totalReturns?: number;
  totalNetSales?: number;
  vatSummary?: unknown;
  paymentMethodsData?: unknown;
  salesCategories?: unknown;
};

export class InvoiceUploadService {
  private paymentService: PaymentService;
  // FIN-869: constructed directly, not via ServiceRegistry (that edge was circular — see PR description).
  private approvalService: ApprovalService;

  constructor() {
    this.paymentService = new PaymentService();
    this.approvalService = new ApprovalService();
  }

  /**
   * Upload an invoice PDF file and store its metadata
   */
  public async uploadInvoice(data: InvoiceFormData): Promise<{ success: boolean; invoice?: Invoice }> {
    try {
      // Get the current authenticated user
      const { data: authData } = await supabase.auth.getUser();
      if (!authData.user) {
        toast.error(translateOutsideReact('consultantToasts.upload.notLoggedIn'));
        return { success: false };
      }

      // Get the current company ID
      const { data: companyId, error: companyError } = await supabase.rpc('current_company_id');
      if (companyError || !companyId) {
        toast.error(translateOutsideReact('consultantToasts.upload.noCompanyContext'));
        return { success: false };
      }

      const file = data.file;

      // Create a unique file path using the company ID and a timestamp
      const filePath = `${companyId}/${Date.now()}-${file.name.replace(/\s+/g, '_')}`;

      // Upload the file to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('invoices')
        .upload(filePath, file);
      
      if (uploadError) {
        console.error("Error uploading file:", uploadError);
        toast.error(translateOutsideReact('consultantToasts.upload.fileUploadFailed'));
        return { success: false };
      }
      
      // Get the public URL for the file
      const { data: fileData } = supabase.storage
        .from('invoices')
        .getPublicUrl(filePath);
      
      // Insert invoice metadata into the database
      const { data: invoiceData, error: insertError } = await supabase
        .from('financial_documents')
        .insert({
          user_id: authData.user.id,
          company_id: companyId,
          vendor: data.vendor,
          amount: data.amount,
          currency: data.currency,
          // "" is the form's untouched date (a receipt has none), not a date (FIN-1100).
          due_date: data.dueDate || null,
          transaction_date: data.transactionDate,
          file_path: filePath,
          file_name: file.name,
          document_type: data.documentType,
          remittance_information: data.remittanceInformation,
          bank_bic: data.bankBic,
          bank_name: data.bankName
        } as any)
        .select()
        .single();
      
      if (insertError) {
        console.error("Error inserting invoice data:", insertError);
        toast.error(translateOutsideReact('consultantToasts.upload.saveFailed'));
        
        // Clean up the uploaded file if we couldn't save the metadata
        await supabase.storage
          .from('invoices')
          .remove([filePath]);
          
        return { success: false };
      }
      
      // If this is a payable document and has payable data, create the payable record
      if (data.documentType === 'payable' && (data.vendorAccountFields || data.sourceAccountId || data.payablePaymentDate)) {
        try {
          await this.paymentService.createPayable({
            invoice_id: invoiceData.id,
            currency: data.currency,
            amount: data.amount,
            ...data.vendorAccountFields,
            // FIN-776: the upload form's Source Account picker renders one SelectItem per
            // connected account with `value={account.id}`, so this is always a real
            // bank_accounts.id — it just never reached the FK column before.
            source_account_id: data.sourceAccountId || null,
            payment_date: data.payablePaymentDate
          });
          console.log("Payable record created successfully");
        } catch (payableError) {
          // Don't fail the entire operation if payable creation fails
          await handleError(payableError, translateOutsideReact('consultantErrors.fallback.invoiceUploadedButPayableRecordCreationFailed'), "uploadInvoice:createPayable");
        }
      }
      
      // Format the invoice for the frontend
      const invoice: Invoice = {
        id: invoiceData.id,
        vendor: invoiceData.vendor,
        amount: invoiceData.amount,
        currency: invoiceData.currency,
        dueDate: invoiceData.due_date,
        status: "unpaid", // Default status for new invoices
        fxRate: 1.0, // Default fx rate
        remittanceInformation: invoiceData.remittance_information,
        ...(data.vendorAccountFields && {
          vendorIban: data.vendorAccountFields.vendor_iban,
          vendorBankgiro: data.vendorAccountFields.vendor_bankgiro,
          vendorPlusgiro: data.vendorAccountFields.vendor_plusgiro,
          vendorAccountNumber: data.vendorAccountFields.vendor_account_number,
          vendorClearingNumber: data.vendorAccountFields.vendor_clearing_number,
        }),
        sourceAccountId: data.sourceAccountId,
        payablePaymentDate: data.payablePaymentDate
      };

      // Auto-approve the invoice if approval flow is disabled (skip for expenses and receipts)
      if (!isApprovalFlowEnabled() && data.documentType !== 'expense' && data.documentType !== 'receipt') {
        try {
          const approvalResult = await this.approvalService.approveInvoice(invoiceData.id);
          if (approvalResult.success) {
            console.log("Invoice auto-approved successfully");
            // Update the invoice object with approval information if available
            if (approvalResult.invoice) {
              Object.assign(invoice, approvalResult.invoice);
            }
          } else {
            console.warn("Auto-approval failed, but invoice was created successfully");
            // Don't fail the entire operation if auto-approval fails
          }
        } catch (autoApprovalError) {
          console.error("Error during auto-approval:", autoApprovalError);
          // Don't fail the entire operation if auto-approval fails
        }
      }

      toast.success(translateOutsideReact('consultantToasts.upload.uploaded'));
      return { success: true, invoice };
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToProcessYourInvoiceUpload'), "uploadInvoice");
      return { success: false };
    }
  }
  
  /**
   * Get the file URL for an invoice
   */
  public async getInvoiceFileUrl(filePath: string): Promise<string | null> {
    try {
      const { data } = supabase.storage
        .from('invoices')
        .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {
      console.error("Error getting file URL:", error);
      return null;
    }
  }

  /**
   * Upload file only to storage (for extraction workflow)
   * Supports PDF and images (JPG, PNG)
   */
  public async uploadFileOnly(file: File): Promise<{ filePath: string; companyId: string }> {
    // Get the current authenticated user
    const { data: authData } = await supabase.auth.getUser();
    if (!authData.user) {
      throw new Error("You must be logged in to upload files");
    }

    // Get the current company ID
    const { data: companyId, error: companyError } = await supabase.rpc('current_company_id');
    if (companyError || !companyId) {
      throw new Error("Unable to determine company context");
    }

    // Create a unique file path
    const filePath = `${companyId}/${Date.now()}-${file.name.replace(/\s+/g, '_')}`;

    // Upload the file to Supabase storage
    const { error: uploadError } = await supabase.storage
      .from('invoices')
      .upload(filePath, file);

    if (uploadError) {
      console.error("Error uploading file:", uploadError);
      throw new Error("Failed to upload the invoice file");
    }

    return { filePath, companyId };
  }

  /**
   * @deprecated Use uploadFileOnly instead
   * Upload PDF only to storage (for extraction workflow)
   */
  public async uploadPdfOnly(file: File): Promise<{ filePath: string; companyId: string }> {
    return this.uploadFileOnly(file);
  }

  /**
   * Extract invoice data with retry logic
   */
  public async extractWithRetry(
    filePath: string,
    documentType: 'payable' | 'receivable' | 'receipt' | 'expense',
    maxRetries: number = 3
  ): Promise<{ data: Record<string, any>; metadata: any; vendorAccountFields?: { vendor_iban?: string | null; vendor_bankgiro?: string | null; vendor_plusgiro?: string | null; vendor_account_number?: string | null; vendor_clearing_number?: string | null; } | null }> {
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`Extraction attempt ${attempt}/${maxRetries}`);
        const result = await InvoiceApiClient.extractInvoiceData(filePath, documentType);

        // Check if extraction is acceptable
        if (result.metadata.extractionStatus === 'completed' ||
            result.metadata.extractionStatus === 'partial' ||
            result.metadata.confidence >= 0.3) {
          return result; // Success or acceptable partial
        }

        // Low confidence, retry
        if (attempt < maxRetries) {
          await this.sleep(1000 * attempt); // Exponential backoff
          console.log(`Retrying extraction due to low confidence...`);
        }
      } catch (error) {
        lastError = error as Error;
        console.error(`Extraction attempt ${attempt} failed:`, error);
        if (attempt < maxRetries) {
          await this.sleep(1000 * attempt);
        }
      }
    }

    // All retries failed
    throw lastError || new Error('Extraction failed after retries');
  }

  /**
   * Create draft invoice with extracted data
   */
  public async createDraftInvoice(params: {
    extractedData: Record<string, any>;
    extractionMetadata: any;
    filePath: string;
    fileName: string;
    documentType: 'payable' | 'receivable' | 'receipt' | 'expense';
    vendorAccountFields?: {
      vendor_iban?: string | null;
      vendor_bankgiro?: string | null;
      vendor_plusgiro?: string | null;
      vendor_account_number?: string | null;
      vendor_clearing_number?: string | null;
    } | null;
    categoryMetadata?: CategoryMetadata;
  }): Promise<{ invoiceId: string }> {
    const { data: companyId } = await supabase.rpc('current_company_id');
    if (!companyId) {
      throw new Error("Unable to determine company context");
    }

    return InvoiceApiClient.createDraftInvoice({
      invoiceData: params.extractedData,
      extractionMetadata: params.extractionMetadata,
      filePath: params.filePath,
      fileName: params.fileName,
      companyId,
      documentType: params.documentType,
      vendorAccountFields: params.vendorAccountFields,
      categoryMetadata: params.categoryMetadata,
    });
  }

  /**
   * Update draft invoice with extraction results
   */
  public async updateDraftExtraction(params: {
    invoiceId: string;
    extractedData: Record<string, any>;
    extractionMetadata: any;
    vendorAccountFields?: {
      vendor_iban?: string | null;
      vendor_bankgiro?: string | null;
      vendor_plusgiro?: string | null;
      vendor_account_number?: string | null;
      vendor_clearing_number?: string | null;
    } | null;
  }): Promise<{ success: boolean }> {
    return InvoiceApiClient.updateDraftExtraction(params);
  }

  /**
   * Finalize draft invoice with user edits
   */
  public async finalizeDraftInvoice(
    invoiceId: string,
    updates: DraftFinalizeUpdates
  ): Promise<{ success: boolean; invoice?: Invoice; error?: string; alreadyFinalized?: boolean }> {
    // Convert InvoiceFormData to database column names
    const dbUpdates: Record<string, any> = {};

    if (updates.vendor !== undefined) dbUpdates.vendor = updates.vendor;
    if (updates.amount !== undefined) dbUpdates.amount = updates.amount;
    if (updates.currency !== undefined) dbUpdates.currency = updates.currency;
    // "" is the form's untouched date, not a date — finalize_document_atomic would reject it.
    if (updates.dueDate) dbUpdates.due_date = updates.dueDate;
    if (updates.transactionDate) dbUpdates.transaction_date = updates.transactionDate;
    if (updates.remittanceInformation !== undefined) dbUpdates.remittance_information = updates.remittanceInformation;
    if (updates.bankBic !== undefined) dbUpdates.bank_bic = updates.bankBic;
    if (updates.bankName !== undefined) dbUpdates.bank_name = updates.bankName;
    if (updates.vendorAccountFields !== undefined) {
      if (updates.vendorAccountFields?.vendor_iban !== undefined) dbUpdates.vendor_iban = updates.vendorAccountFields.vendor_iban;
      if (updates.vendorAccountFields?.vendor_bankgiro !== undefined) dbUpdates.vendor_bankgiro = updates.vendorAccountFields.vendor_bankgiro;
      if (updates.vendorAccountFields?.vendor_plusgiro !== undefined) dbUpdates.vendor_plusgiro = updates.vendorAccountFields.vendor_plusgiro;
      if (updates.vendorAccountFields?.vendor_account_number !== undefined) dbUpdates.vendor_account_number = updates.vendorAccountFields.vendor_account_number;
      if (updates.vendorAccountFields?.vendor_clearing_number !== undefined) dbUpdates.vendor_clearing_number = updates.vendorAccountFields.vendor_clearing_number;
    }
    // The picker's untouched value is "" — no account, not an id. Only a payable pays from an
    // account, and the picker keeps its value when the type is switched away from payable.
    if (updates.sourceAccountId && updates.documentType === 'payable') dbUpdates.source_account_id = updates.sourceAccountId;
    if (updates.paymentMethod !== undefined) dbUpdates.payment_method = updates.paymentMethod;
    if (updates.expenseCategory !== undefined) dbUpdates.expense_category = updates.expenseCategory;
    if (updates.categoryMetadata !== undefined) dbUpdates.category_metadata = updates.categoryMetadata;
    if (updates.documentType !== undefined) dbUpdates.document_type = updates.documentType;
    // Sales report fields (passed through to backend for sales_reports record creation)
    if (updates.reportDate !== undefined) dbUpdates.report_date = updates.reportDate;
    if (updates.totalGrossSales !== undefined) dbUpdates.total_gross_sales = updates.totalGrossSales;
    if (updates.totalReturns !== undefined) dbUpdates.total_returns = updates.totalReturns;
    if (updates.totalNetSales !== undefined) dbUpdates.total_net_sales = updates.totalNetSales;
    if (updates.vatSummary !== undefined) dbUpdates.vat_summary = updates.vatSummary;
    if (updates.paymentMethodsData !== undefined) dbUpdates.payment_methods_data = updates.paymentMethodsData;
    if (updates.salesCategories !== undefined) dbUpdates.sales_categories = updates.salesCategories;

    const result = await InvoiceApiClient.finalizeDraftInvoice(invoiceId, dbUpdates);

    if (result.success) {
      toast.success(translateOutsideReact('consultantToasts.upload.finalized'));
    } else if (result.error && !result.alreadyFinalized) {
      // Show validation error to user. Skip when the document was already
      // finalized by a concurrent process (e.g. the background extraction
      // job) — the caller (DraftDetailDialog) shows its own honest message
      // for that case instead of a generic error toast.
      toast.error(friendlyError(result.error));
    }

    // Auto-approval (when the approval flow is disabled) is handled server-side
    // in invoiceService.finalizeDraftInvoice() — it applies uniformly whether
    // this call originates here or from the extraction pipeline's auto-finalize.
    return result;
  }

  /**
   * Upload multiple files in bulk with concurrency limiting.
   * Phase 1: Upload files to storage (concurrency 3)
   * Phase 2: Batch-create drafts for successful uploads
   * Phase 3: Merge results and cleanup orphaned files
   */
  async uploadMultipleFiles(
    files: File[],
    documentType: 'payable' | 'receivable' | 'receipt' | 'expense' | 'unknown',
    onProgress?: (index: number, status: 'uploading' | 'uploaded' | 'success' | 'error', error?: string) => void
  ): Promise<Array<{ draftId: string; fileName: string } | { error: string; fileName: string }>> {
    const CONCURRENCY = 3;

    // Phase 1: Upload files to storage in parallel (concurrency limited)
    let resolvedCompanyId: string | null = null;
    const uploadResults: Array<{ filePath: string; fileName: string } | { error: string; fileName: string }> = [];
    for (let i = 0; i < files.length; i += CONCURRENCY) {
      const batch = files.slice(i, i + CONCURRENCY);
      const batchResults = await Promise.allSettled(
        batch.map(async (file, batchIdx) => {
          const globalIdx = i + batchIdx;
          onProgress?.(globalIdx, 'uploading');
          const { filePath, companyId } = await this.uploadFileOnly(file);
          if (!resolvedCompanyId) resolvedCompanyId = companyId;
          onProgress?.(globalIdx, 'uploaded');
          return { filePath, fileName: file.name };
        })
      );
      for (let j = 0; j < batchResults.length; j++) {
        const result = batchResults[j];
        const globalIdx = i + j;
        if (result.status === 'fulfilled') {
          uploadResults.push(result.value);
        } else {
          const errorMsg = result.reason instanceof Error ? result.reason.message : 'Upload failed';
          uploadResults.push({ error: errorMsg, fileName: files[globalIdx].name });
          onProgress?.(globalIdx, 'error', errorMsg);
        }
      }
    }

    // Phase 2: Batch-create drafts for successfully uploaded files
    const successfulUploads = uploadResults.filter(
      (r): r is { filePath: string; fileName: string } => !('error' in r)
    );

    if (successfulUploads.length === 0 || !resolvedCompanyId) {
      return uploadResults as Array<{ error: string; fileName: string }>;
    }

    const batchResult = await InvoiceApiClient.createBatchDrafts(
      successfulUploads.map(u => ({ ...u, documentType })),
      resolvedCompanyId
    );

    // Phase 3: Merge results — clean up orphaned files for failed drafts
    const finalResults: Array<{ draftId: string; fileName: string } | { error: string; fileName: string }> = [];
    let successIdx = 0;
    for (const uploadResult of uploadResults) {
      if ('error' in uploadResult) {
        finalResults.push(uploadResult);
      } else {
        const draftResult = batchResult.results[successIdx++];
        if ('error' in draftResult) {
          await this.cleanupOrphanedFile(uploadResult.filePath);
        }
        finalResults.push(draftResult);
        const globalIdx = uploadResults.indexOf(uploadResult);
        onProgress?.(globalIdx, 'error' in draftResult ? 'error' : 'success',
          'error' in draftResult ? draftResult.error : undefined);
      }
    }
    return finalResults;
  }

  /**
   * Remove an orphaned file from storage (e.g., when draft creation fails)
   */
  public async cleanupOrphanedFile(filePath: string): Promise<void> {
    try {
      const { error } = await supabase.storage
        .from('invoices')
        .remove([filePath]);
      if (error) {
        console.error("Failed to cleanup orphaned file:", filePath, error);
      } else {
        console.log("Cleaned up orphaned file:", filePath);
      }
    } catch (error) {
      console.error("Error cleaning up orphaned file:", filePath, error);
    }
  }

  async deleteDocument(documentId: string): Promise<void> {
    await InvoiceApiClient.deleteDocument(documentId);
  }

  async getRejectedDocuments(companyId: string): Promise<Invoice[]> {
    const result = await InvoiceApiClient.getRejectedDocuments(companyId);
    return result.invoices;
  }

  async markRejectedSeen(ids: string[]): Promise<void> {
    await InvoiceApiClient.markRejectedSeen(ids);
  }

  async getUnseenRejectedCount(companyId: string): Promise<number> {
    const result = await InvoiceApiClient.getUnseenRejectedCount(companyId);
    return result.count;
  }

  async getRejectedDocumentCompare(
    rejectedId: string,
    companyId: string
  ): Promise<{ rejected: Invoice; original: Invoice | null }> {
    return InvoiceApiClient.getRejectedDocumentCompare(rejectedId, companyId);
  }

  /**
   * Helper: Sleep for specified milliseconds
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
